import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Activity as ActivityIcon, Download, Lock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  "raffle.created": { label: "Rifa creada", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "raffle.updated": { label: "Rifa editada", c: "bg-sky-100 text-sky-700" },
  "raffle.activated": { label: "Rifa publicada", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "raffle.closed": { label: "Ventas cerradas", c: "bg-amber-100 text-amber-700" },
  "raffle.cancelled": { label: "Rifa cancelada", c: "bg-red-100 text-red-700" },
  "raffle.archived": { label: "Rifa archivada", c: "bg-neutral-200 text-neutral-700" },
  "raffle.active": { label: "Rifa reactivada", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "raffle.drawn": { label: "Sorteo ejecutado", c: "bg-amber-100 text-amber-800" },
  "raffle.deleted": { label: "Rifa eliminada", c: "bg-red-100 text-red-700" },
  "order.approved": { label: "Pago aprobado", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "order.rejected": { label: "Pago rechazado", c: "bg-red-100 text-red-700" },
  "settings.updated": { label: "Marca / Perfil", c: "bg-sky-100 text-sky-700" },
  "team.member_added": { label: "Asistente agregado", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "team.member_updated": { label: "Asistente editado", c: "bg-sky-100 text-sky-700" },
  "team.member_removed": { label: "Asistente removido", c: "bg-red-100 text-red-700" },
  "organizer.created": { label: "Organizador creado", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "organizer.updated": { label: "Organizador modificado", c: "bg-sky-100 text-sky-700" },
  "organizer.suspended": { label: "Organizador suspendido", c: "bg-red-100 text-red-700" },
  "organizer.activated": { label: "Organizador activado", c: "bg-[#E6F7FE] text-[#0092CC]" },
  "organizer.deleted": { label: "Organizador eliminado", c: "bg-red-100 text-red-700" },
  "account.created": { label: "Nueva cuenta", c: "bg-emerald-100 text-emerald-700" },
  "number.deleted": { label: "Número eliminado", c: "bg-red-100 text-red-700" },
  "order.deleted": { label: "Registro eliminado", c: "bg-red-100 text-red-700" },
};
const meta = (a) => ACTION_META[a] || { label: a, c: "bg-neutral-100 text-neutral-600" };
const roleLabel = { super_admin: "Super-admin", owner: "Dueño", assistant: "Asistente" };
const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

export default function Activity() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_super_admin;
  const isFull = isAdmin || user?.plan === "full";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { action: filter } : {};
      const res = await api.get(isAdmin ? "/admin/activity" : "/activity", { params });
      setLogs(res.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("No se pudo cargar la actividad");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isFull) load(); else setLoading(false); /* eslint-disable-next-line */ }, [filter]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/activity/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "actividad.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el CSV"); }
  };

  if (!isFull) {
    return (
      <DashboardLayout title="Actividad">
        <div className="max-w-md mx-auto text-center py-20" data-testid="activity-locked">
          <div className="h-14 w-14 rounded-2xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mx-auto mb-4"><Lock size={26} /></div>
          <h2 className="font-display font-bold text-xl">Historial de cambios</h2>
          <p className="text-neutral-500 mt-2">El historial de modificaciones de tu cuenta y tus asistentes está disponible con el <b>Acceso Completo</b>.</p>
          <Button data-testid="activity-request-full" onClick={() => navigate("/dashboard")} className="mt-5 rounded-xl bg-[#00AEEF] hover:bg-[#0092CC]">Solicitar Acceso Completo <ArrowRight size={16} className="ml-1" /></Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Actividad">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-neutral-900 text-white flex items-center justify-center"><ActivityIcon size={18} /></div>
          <p className="text-sm text-neutral-500">{isAdmin ? "Historial global de cambios de toda la plataforma." : "Historial de cambios de tu cuenta y tus asistentes."}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="activity-filter" className="rounded-xl w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las acciones</SelectItem>
              {Object.keys(ACTION_META).map((a) => <SelectItem key={a} value={a}>{ACTION_META[a].label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && <Button data-testid="activity-csv-btn" onClick={downloadCsv} variant="outline" className="rounded-xl gap-2 text-[#0092CC] border-[#B3E7FB]"><Download size={16} /> CSV</Button>}
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-neutral-400" /></div>
      ) : logs.length === 0 ? (
        <div className="py-16 text-center text-neutral-500" data-testid="activity-empty">
          <ActivityIcon size={40} className="mx-auto mb-3 text-neutral-300" />
          Aún no hay actividad registrada.
        </div>
      ) : (
        <div className="space-y-2" data-testid="activity-list">
          {logs.map((l) => {
            const m = meta(l.action);
            return (
              <div key={l.id} data-testid={`activity-item-${l.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.c}`}>{m.label}</span>
                      <span className="text-sm font-medium text-neutral-800">{l.actor_name || l.actor_email}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">{roleLabel[l.actor_role] || l.actor_role}</span>
                    </div>
                    <p className="text-sm text-neutral-600 mt-1">{l.summary}</p>
                    {l.changes?.length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        {l.changes.map((c, i) => (
                          <p key={i} className="text-xs text-neutral-500">
                            <b className="text-neutral-600">{c.field}:</b> <span className="line-through text-neutral-400">{fmtVal(c.before)}</span> <ArrowRight size={10} className="inline mx-1" /> <span className="text-neutral-700">{fmtVal(c.after)}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-neutral-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString("es-CR")}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
